/*
 * QR-Code-Erzeuger des Kits (Plan 2026-10-13-antd-ausbau, Stufe 8; ersetzt die Antd-`QRCode`-Abhängigkeit).
 * Verfahren nach ISO/IEC 18004 in der Ablaufform der Bibliothek „QR Code generator“ von Project Nayuki (MIT), die
 * Antd verwendet: Segmentwahl (Ziffern, alphanumerisch, Bytes), kleinste passende Version, Fehlerkorrektur bei
 * Platz angehoben, Maske mit geringster Strafpunktzahl. Ergebnis identisch mit der bisherigen Antd-Ausgabe
 * (geprüft gegen `@rc-component/qrcode` vor der Entfernung).
 */

export type QrErrorLevel = "L" | "M" | "Q" | "H";

const ECC: Record<QrErrorLevel, { ordinal: number; formatBits: number }> = {
  L: { ordinal: 0, formatBits: 1 },
  M: { ordinal: 1, formatBits: 0 },
  Q: { ordinal: 2, formatBits: 3 },
  H: { ordinal: 3, formatBits: 2 },
};

// Tabellen der Norm: Fehlerkorrektur-Codewörter je Block und Anzahl Blöcke, je Stufe (L, M, Q, H) und Version (1–40).
const ECC_CODEWORDS_PER_BLOCK = [
  [-1, 7, 10, 15, 20, 26, 18, 20, 24, 30, 18, 20, 24, 26, 30, 22, 24, 28, 30, 28, 28, 28, 28, 30, 30, 26, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28],
  [-1, 13, 22, 18, 26, 18, 24, 18, 22, 20, 24, 28, 26, 24, 20, 30, 24, 28, 28, 26, 30, 28, 30, 30, 30, 30, 28, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
  [-1, 17, 28, 22, 16, 22, 28, 26, 26, 24, 28, 24, 28, 22, 24, 24, 30, 28, 28, 26, 28, 30, 24, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30, 30],
];
const NUM_ERROR_CORRECTION_BLOCKS = [
  [-1, 1, 1, 1, 1, 1, 2, 2, 2, 2, 4, 4, 4, 4, 4, 6, 6, 6, 6, 7, 8, 8, 9, 9, 10, 12, 12, 12, 13, 14, 15, 16, 17, 18, 19, 19, 20, 21, 22, 24, 25],
  [-1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49],
  [-1, 1, 1, 2, 2, 4, 4, 6, 6, 8, 8, 8, 10, 12, 16, 12, 17, 16, 18, 21, 20, 23, 23, 25, 27, 29, 34, 34, 35, 38, 40, 43, 45, 48, 51, 53, 56, 59, 62, 65, 68],
  [-1, 1, 1, 2, 4, 4, 4, 5, 6, 8, 8, 11, 11, 16, 16, 18, 16, 19, 21, 25, 25, 25, 34, 30, 32, 35, 37, 40, 42, 45, 48, 51, 54, 57, 60, 63, 66, 70, 74, 77, 81],
];

// Benannte Größen der Norm ISO/IEC 18004.
const BITS_PER_BYTE = 8;
const BYTE_TOP_BIT_SHIFT = BITS_PER_BYTE - 1;
const BYTE_INDEX_SHIFT = 3;
const MAX_VERSION = 40;
const MODE_INDICATOR_BITS = 4;
const TERMINATOR_MAX_BITS = 4;
const NUMERIC_GROUP_DIGITS = 3;
const NUMERIC_BITS_PER_DIGIT = 3;
const ALPHANUMERIC_BASE = 45;
const ALPHANUMERIC_PAIR_LENGTH = 2;
const ALPHANUMERIC_PAIR_BITS = 11;
const ALPHANUMERIC_SINGLE_BITS = 6;
const COUNT_BITS_CLASS_OFFSET = 7;
const COUNT_BITS_CLASS_WIDTH = 17;
const RAW_MODULES_QUADRATIC = 16;
const RAW_MODULES_LINEAR = 128;
const RAW_MODULES_BASE = 64;
const ALIGNMENT_MIN_VERSION = 2;
const ALIGNMENT_VERSION_SPAN = 7;
const ALIGNMENT_BASE_COUNT = 2;
const ALIGNMENT_AREA_QUADRATIC = 25;
const ALIGNMENT_AREA_LINEAR = 10;
const ALIGNMENT_AREA_BASE = 55;
const ALIGNMENT_STEP_UNIT = 2;
const ALIGNMENT_SPECIAL_VERSION = 32;
const ALIGNMENT_SPECIAL_STEP = 26;
const ALIGNMENT_LAST_OFFSET = 7;
const ALIGNMENT_RADIUS = 2;
const VERSION_INFO_MIN_VERSION = 7;
const VERSION_INFO_MODULES = 36;
const VERSION_BCH_DEGREE = 12;
const VERSION_BCH_POLY = 0x1f25;
const VERSION_INFO_BITS = 18;
const VERSION_INFO_ROWS = 3;
const VERSION_INFO_OFFSET = 11;
const GF_POLYNOMIAL = 0x11d;
const GF_GENERATOR = 0x02;
const PAD_BYTE_EVEN = 0xec;
const PAD_BYTE_ODD = 0x11;
const PAD_BYTE_TOGGLE = PAD_BYTE_EVEN ^ PAD_BYTE_ODD;
const MODULES_PER_VERSION = 4;
const BASE_MODULES = 17;
const FORMAT_ECC_SHIFT = 3;
const FORMAT_BCH_DEGREE = 10;
const FORMAT_BCH_POLY = 0x537;
const FORMAT_XOR_MASK = 0x5412;
const FORMAT_BIT_COUNT = 15;
const FINDER_CENTER = 3;
const FINDER_RADIUS = 4;
const FINDER_LIGHT_RING = 2;
const TIMING_LINE = 6;
const MASK_COUNT = 8;
const ZIGZAG_COLUMNS = 2;
const ZIGZAG_DIRECTION_BIT = 2;
const BYTE_BIT_MASK = 7;
const PENALTY_PASSES = 2;
const PENALTY_RUN_LENGTH = 5;
const PENALTY_RUN = 3;
const PENALTY_BLOCK = 3;
const PENALTY_FINDER = 40;
const PENALTY_BALANCE = 10;
const BALANCE_DARK_WEIGHT = 20;
const BALANCE_TOTAL_WEIGHT = 10;
const FINDER_CORE_RATIO = 3;
const FINDER_BORDER_RATIO = 4;

type Mode = { bits: number; countBits: [number, number, number] };
const NUMERIC: Mode = { bits: 0x1, countBits: [10, 12, 14] };
const ALPHANUMERIC: Mode = { bits: 0x2, countBits: [9, 11, 13] };
const BYTE: Mode = { bits: 0x4, countBits: [8, 16, 16] };
const ALPHANUMERIC_CHARSET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ $%*+-./:";

type Segment = { mode: Mode; count: number; bits: number[] };

function pushBits(value: number, length: number, target: number[]) {
  for (let i = length - 1; i >= 0; i--) target.push((value >>> i) & 1);
}

const bit = (value: number, index: number) => ((value >>> index) & 1) !== 0;
const countBits = (mode: Mode, version: number) => mode.countBits[Math.floor((version + COUNT_BITS_CLASS_OFFSET) / COUNT_BITS_CLASS_WIDTH)];

function makeSegments(text: string): Segment[] {
  if (text === "") return [];
  const bits: number[] = [];
  if (/^[0-9]*$/.test(text)) {
    for (let i = 0; i < text.length; i += NUMERIC_GROUP_DIGITS) {
      const length = Math.min(NUMERIC_GROUP_DIGITS, text.length - i);
      pushBits(Number(text.slice(i, i + length)), length * NUMERIC_BITS_PER_DIGIT + 1, bits);
    }
    return [{ mode: NUMERIC, count: text.length, bits }];
  }
  if (/^[A-Z0-9 $%*+./:-]*$/.test(text)) {
    let i = 0;
    for (; i + ALPHANUMERIC_PAIR_LENGTH <= text.length; i += ALPHANUMERIC_PAIR_LENGTH) pushBits(ALPHANUMERIC_CHARSET.indexOf(text[i]) * ALPHANUMERIC_BASE + ALPHANUMERIC_CHARSET.indexOf(text[i + 1]), ALPHANUMERIC_PAIR_BITS, bits);
    if (i < text.length) pushBits(ALPHANUMERIC_CHARSET.indexOf(text[i]), ALPHANUMERIC_SINGLE_BITS, bits);
    return [{ mode: ALPHANUMERIC, count: text.length, bits }];
  }
  const bytes = new TextEncoder().encode(text);
  bytes.forEach((byte) => pushBits(byte, BITS_PER_BYTE, bits));
  return [{ mode: BYTE, count: bytes.length, bits }];
}

function rawDataModules(version: number) {
  let result = (RAW_MODULES_QUADRATIC * version + RAW_MODULES_LINEAR) * version + RAW_MODULES_BASE;
  if (version >= ALIGNMENT_MIN_VERSION) {
    const align = Math.floor(version / ALIGNMENT_VERSION_SPAN) + ALIGNMENT_BASE_COUNT;
    result -= (ALIGNMENT_AREA_QUADRATIC * align - ALIGNMENT_AREA_LINEAR) * align - ALIGNMENT_AREA_BASE;
    if (version >= VERSION_INFO_MIN_VERSION) result -= VERSION_INFO_MODULES;
  }
  return result;
}

const dataCodewords = (version: number, ecc: number) =>
  Math.floor(rawDataModules(version) / BITS_PER_BYTE) - ECC_CODEWORDS_PER_BLOCK[ecc][version] * NUM_ERROR_CORRECTION_BLOCKS[ecc][version];

function totalBits(segments: Segment[], version: number) {
  let result = 0;
  for (const segment of segments) {
    const ccBits = countBits(segment.mode, version);
    if (segment.count >= 1 << ccBits) return Infinity;
    result += MODE_INDICATOR_BITS + ccBits + segment.bits.length;
  }
  return result;
}

function gfMultiply(x: number, y: number) {
  let z = 0;
  for (let i = BYTE_TOP_BIT_SHIFT; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> BYTE_TOP_BIT_SHIFT) * GF_POLYNOMIAL);
    z ^= ((y >>> i) & 1) * x;
  }
  return z;
}

function rsDivisor(degree: number) {
  const result: number[] = Array.from({ length: degree - 1 }, () => 0).concat([1]);
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < result.length; j++) {
      result[j] = gfMultiply(result[j], root);
      if (j + 1 < result.length) result[j] ^= result[j + 1];
    }
    root = gfMultiply(root, GF_GENERATOR);
  }
  return result;
}

function rsRemainder(data: number[], divisor: number[]) {
  const result = divisor.map(() => 0);
  for (const byte of data) {
    const factor = byte ^ (result.shift() as number);
    result.push(0);
    divisor.forEach((coefficient, i) => { result[i] ^= gfMultiply(coefficient, factor); });
  }
  return result;
}

/** Erzeugt die Modulmatrix (true = dunkel) für einen Text. */
export function encodeQr(text: string, level: QrErrorLevel = "M"): boolean[][] {
  const segments = makeSegments(text);
  let ecc = ECC[level];
  let version = 1;
  let usedBits = 0;
  for (; ; version++) {
    const capacity = dataCodewords(version, ecc.ordinal) * BITS_PER_BYTE;
    usedBits = totalBits(segments, version);
    if (usedBits <= capacity) break;
    if (version >= MAX_VERSION) throw new RangeError("QR-Code: Text zu lang");
  }
  for (const next of [ECC.M, ECC.Q, ECC.H]) if (usedBits <= dataCodewords(version, next.ordinal) * BITS_PER_BYTE) ecc = next;

  const bits: number[] = [];
  for (const segment of segments) {
    pushBits(segment.mode.bits, MODE_INDICATOR_BITS, bits);
    pushBits(segment.count, countBits(segment.mode, version), bits);
    bits.push(...segment.bits);
  }
  const capacity = dataCodewords(version, ecc.ordinal) * BITS_PER_BYTE;
  pushBits(0, Math.min(TERMINATOR_MAX_BITS, capacity - bits.length), bits);
  pushBits(0, (BITS_PER_BYTE - (bits.length % BITS_PER_BYTE)) % BITS_PER_BYTE, bits);
  for (let pad = PAD_BYTE_EVEN; bits.length < capacity; pad ^= PAD_BYTE_TOGGLE) pushBits(pad, BITS_PER_BYTE, bits);
  const data: number[] = [];
  for (let i = 0; i < bits.length; i += BITS_PER_BYTE) data.push(bits.slice(i, i + BITS_PER_BYTE).reduce((acc, value) => (acc << 1) | value, 0));

  const size = version * MODULES_PER_VERSION + BASE_MODULES;
  const modules: boolean[][] = Array.from({ length: size }, () => Array.from({ length: size }, () => false));
  const isFunction: boolean[][] = Array.from({ length: size }, () => Array.from({ length: size }, () => false));
  const setFunction = (x: number, y: number, dark: boolean) => { modules[y][x] = dark; isFunction[y][x] = true; };

  const drawFormat = (mask: number) => {
    const value = (ecc.formatBits << FORMAT_ECC_SHIFT) | mask;
    let remainder = value;
    for (let i = 0; i < FORMAT_BCH_DEGREE; i++) remainder = (remainder << 1) ^ ((remainder >>> (FORMAT_BCH_DEGREE - 1)) * FORMAT_BCH_POLY);
    const format = ((value << FORMAT_BCH_DEGREE) | remainder) ^ FORMAT_XOR_MASK;
    for (let i = 0; i <= 5; i++) setFunction(8, i, bit(format, i)); // literal-exception: Koordinaten der Formatinformation, ISO/IEC 18004 Abschnitt 7.9
    setFunction(8, 7, bit(format, 6)); // literal-exception: Koordinate und Bitposition der Formatinformation, ISO/IEC 18004 Abschnitt 7.9
    setFunction(8, 8, bit(format, 7)); // literal-exception: Koordinate und Bitposition der Formatinformation, ISO/IEC 18004 Abschnitt 7.9
    setFunction(7, 8, bit(format, 8)); // literal-exception: Koordinate und Bitposition der Formatinformation, ISO/IEC 18004 Abschnitt 7.9
    for (let i = 9; i < FORMAT_BIT_COUNT; i++) setFunction(14 - i, 8, bit(format, i)); // literal-exception: Koordinate der Formatinformation, ISO/IEC 18004 Abschnitt 7.9
    for (let i = 0; i < BITS_PER_BYTE; i++) setFunction(size - 1 - i, 8, bit(format, i)); // literal-exception: Zeile der Formatinformation, ISO/IEC 18004 Abschnitt 7.9
    for (let i = 8; i < FORMAT_BIT_COUNT; i++) setFunction(8, size - FORMAT_BIT_COUNT + i, bit(format, i)); // literal-exception: Spalte und Startbit der Formatinformation, ISO/IEC 18004 Abschnitt 7.9
    setFunction(8, size - 8, true); // literal-exception: festes dunkles Modul der Formatinformation, ISO/IEC 18004 Abschnitt 7.9
  };

  // Funktionsmuster: Taktlinien, Suchmuster, Ausrichtungsmuster, Format- und Versionsinformation.
  for (let i = 0; i < size; i++) { setFunction(TIMING_LINE, i, (i & 1) === 0); setFunction(i, TIMING_LINE, (i & 1) === 0); }
  const finder = (cx: number, cy: number) => {
    for (let dy = -FINDER_RADIUS; dy <= FINDER_RADIUS; dy++) for (let dx = -FINDER_RADIUS; dx <= FINDER_RADIUS; dx++) {
      const distance = Math.max(Math.abs(dx), Math.abs(dy));
      const x = cx + dx;
      const y = cy + dy;
      if (x >= 0 && x < size && y >= 0 && y < size) setFunction(x, y, distance !== FINDER_LIGHT_RING && distance !== FINDER_RADIUS);
    }
  };
  finder(FINDER_CENTER, FINDER_CENTER);
  finder(size - 1 - FINDER_CENTER, FINDER_CENTER);
  finder(FINDER_CENTER, size - 1 - FINDER_CENTER);
  const alignment: number[] = [];
  if (version > 1) {
    const count = Math.floor(version / ALIGNMENT_VERSION_SPAN) + ALIGNMENT_BASE_COUNT;
    const step = version === ALIGNMENT_SPECIAL_VERSION ? ALIGNMENT_SPECIAL_STEP : Math.ceil((version * MODULES_PER_VERSION + MODULES_PER_VERSION) / ((count - 1) * ALIGNMENT_STEP_UNIT)) * ALIGNMENT_STEP_UNIT;
    alignment.push(TIMING_LINE);
    for (let position = size - ALIGNMENT_LAST_OFFSET; alignment.length < count; position -= step) alignment.splice(1, 0, position);
  }
  alignment.forEach((ay, i) => alignment.forEach((ax, j) => {
    const corner = (i === 0 && j === 0) || (i === 0 && j === alignment.length - 1) || (i === alignment.length - 1 && j === 0);
    if (corner) return;
    for (let dy = -ALIGNMENT_RADIUS; dy <= ALIGNMENT_RADIUS; dy++) for (let dx = -ALIGNMENT_RADIUS; dx <= ALIGNMENT_RADIUS; dx++) setFunction(ax + dx, ay + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
  }));
  drawFormat(0);
  if (version >= VERSION_INFO_MIN_VERSION) {
    let remainder = version;
    for (let i = 0; i < VERSION_BCH_DEGREE; i++) remainder = (remainder << 1) ^ ((remainder >>> (VERSION_BCH_DEGREE - 1)) * VERSION_BCH_POLY);
    const versionBits = (version << VERSION_BCH_DEGREE) | remainder;
    for (let i = 0; i < VERSION_INFO_BITS; i++) {
      const dark = bit(versionBits, i);
      const a = size - VERSION_INFO_OFFSET + (i % VERSION_INFO_ROWS);
      const b = Math.floor(i / VERSION_INFO_ROWS);
      setFunction(a, b, dark);
      setFunction(b, a, dark);
    }
  }

  // Fehlerkorrektur je Block und Verschränkung.
  const numBlocks = NUM_ERROR_CORRECTION_BLOCKS[ecc.ordinal][version];
  const blockEccLength = ECC_CODEWORDS_PER_BLOCK[ecc.ordinal][version];
  const rawCodewords = Math.floor(rawDataModules(version) / BITS_PER_BYTE);
  const numShortBlocks = numBlocks - (rawCodewords % numBlocks);
  const shortBlockLength = Math.floor(rawCodewords / numBlocks);
  const divisor = rsDivisor(blockEccLength);
  const blocks: number[][] = [];
  for (let i = 0, offset = 0; i < numBlocks; i++) {
    const block = data.slice(offset, offset + shortBlockLength - blockEccLength + (i < numShortBlocks ? 0 : 1));
    offset += block.length;
    const eccPart = rsRemainder(block, divisor);
    if (i < numShortBlocks) block.push(0);
    blocks.push(block.concat(eccPart));
  }
  const codewords: number[] = [];
  for (let i = 0; i < blocks[0].length; i++) blocks.forEach((block, j) => {
    if (i !== shortBlockLength - blockEccLength || j >= numShortBlocks) codewords.push(block[i]);
  });

  // Datenmodule im Zickzack von unten rechts.
  let index = 0;
  for (let right = size - 1; right >= 1; right -= ZIGZAG_COLUMNS) {
    if (right === TIMING_LINE) right = TIMING_LINE - 1;
    for (let vertical = 0; vertical < size; vertical++) for (let j = 0; j < ZIGZAG_COLUMNS; j++) {
      const x = right - j;
      const upward = ((right + 1) & ZIGZAG_DIRECTION_BIT) === 0;
      const y = upward ? size - 1 - vertical : vertical;
      if (!isFunction[y][x] && index < codewords.length * BITS_PER_BYTE) {
        modules[y][x] = bit(codewords[index >>> BYTE_INDEX_SHIFT], BYTE_TOP_BIT_SHIFT - (index & BYTE_BIT_MASK));
        index++;
      }
    }
  }

  const applyMask = (mask: number) => {
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
      let invert: boolean;
      switch (mask) {
        case 0: invert = (x + y) % 2 === 0; break; // literal-exception: Maskenformel nach ISO/IEC 18004 Tabelle 10
        case 1: invert = y % 2 === 0; break; // literal-exception: Maskenformel nach ISO/IEC 18004 Tabelle 10
        case 2: invert = x % 3 === 0; break; // literal-exception: Maskenformel nach ISO/IEC 18004 Tabelle 10
        case 3: invert = (x + y) % 3 === 0; break; // literal-exception: Maskenformel nach ISO/IEC 18004 Tabelle 10
        case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break; // literal-exception: Maskenformel nach ISO/IEC 18004 Tabelle 10
        case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break; // literal-exception: Maskenformel nach ISO/IEC 18004 Tabelle 10
        case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break; // literal-exception: Maskenformel nach ISO/IEC 18004 Tabelle 10
        default: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; // literal-exception: Maskenformel nach ISO/IEC 18004 Tabelle 10
      }
      if (!isFunction[y][x] && invert) modules[y][x] = !modules[y][x];
    }
  };

  const penalty = () => {
    let result = 0;
    const addHistory = (length: number, history: number[]) => {
      if (history[0] === 0) length += size;
      history.pop();
      history.unshift(length);
    };
    const countPatterns = (history: number[]) => {
      const n = history[1];
      const core = n > 0 && history[2] === n && history[3] === n * FINDER_CORE_RATIO && history[4] === n && history[5] === n;
      return (core && history[0] >= n * FINDER_BORDER_RATIO && history[6] >= n ? 1 : 0) + (core && history[6] >= n * FINDER_BORDER_RATIO && history[0] >= n ? 1 : 0);
    };
    const terminate = (color: boolean, length: number, history: number[]) => {
      if (color) { addHistory(length, history); length = 0; }
      length += size;
      addHistory(length, history);
      return countPatterns(history);
    };
    for (let pass = 0; pass < PENALTY_PASSES; pass++) {
      for (let a = 0; a < size; a++) {
        let color = false;
        let run = 0;
        const history = [0, 0, 0, 0, 0, 0, 0];
        for (let b = 0; b < size; b++) {
          const cell = pass === 0 ? modules[a][b] : modules[b][a];
          if (cell === color) {
            run++;
            if (run === PENALTY_RUN_LENGTH) result += PENALTY_RUN;
            else if (run > PENALTY_RUN_LENGTH) result++;
          } else {
            addHistory(run, history);
            if (!color) result += countPatterns(history) * PENALTY_FINDER;
            color = cell;
            run = 1;
          }
        }
        result += terminate(color, run, history) * PENALTY_FINDER;
      }
    }
    for (let y = 0; y < size - 1; y++) for (let x = 0; x < size - 1; x++) {
      const color = modules[y][x];
      if (color === modules[y][x + 1] && color === modules[y + 1][x] && color === modules[y + 1][x + 1]) result += PENALTY_BLOCK;
    }
    const dark = modules.reduce((sum, row) => sum + row.filter(Boolean).length, 0);
    const total = size * size;
    result += (Math.ceil(Math.abs(dark * BALANCE_DARK_WEIGHT - total * BALANCE_TOTAL_WEIGHT) / total) - 1) * PENALTY_BALANCE;
    return result;
  };

  let bestMask = 0;
  let bestPenalty = Infinity;
  for (let mask = 0; mask < MASK_COUNT; mask++) {
    applyMask(mask);
    drawFormat(mask);
    const score = penalty();
    if (score < bestPenalty) { bestMask = mask; bestPenalty = score; }
    applyMask(mask);
  }
  applyMask(bestMask);
  drawFormat(bestMask);
  return modules;
}
